/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / ORDERS-CORRECTION — Voll- und Teilstorno + Korrektur-Bon
   =============================================================================
   S67 Refactor: aus orders.js extrahiert (Zeilen 1309-1971, ~660 LOC).

   Block enthält drei Themen:
     1. VOLL-STORNO (Order-Level)
        - CORRECTION_REASONS, openCorrectionDialog(), closeCorrectionDialog(),
          submitCorrectionDialog()
        - Erzeugt einen vollen Storno-Beleg über die gesamte Order (eigene
          Rechnungsnummer, GoBD-Append-Only, TSE-Signatur async).

     2. TEIL-STORNO (Item-Level, S60 Block S — GoBD §146a AO)
        - PARTIAL_CORRECTION_REASONS, openPartialCorrectionDialog(),
          closePartialCorrectionDialog(), setPartialCorrectionQty(),
          submitCorrectionItemDialog() (S63 1-Klick-Shortcut),
          submitPartialCorrectionDialog()
        - Erzeugt pro Item mit qty>0 eine eigene `order_corrections`-Zeile,
          gemeinsame TSE-Storno-Tx über die Summe.

     3. KORREKTUR-BON-RENDERER (S60 Block S4)
        - buildPartialCorrectionReceiptHtml() (pure)
        - openCorrectionReceiptModal()
        - printCorrectionReceiptToPrinter() (ESC/POS-Druck-Trigger)
        - _archivePartialCorrectionBon() (GoBD §147 AO Persistenz)

   Cross-Module-Calls:
     - closeOrderDetail() → via callCloseOrderDetail aus orders-modal-state.js
       (Anti-Circular, Order-Detail-Modal bleibt in orders.js).
     - _ensureBonArchived() → direkter ESM-Import aus orders-receipt-actions.js
       (kein Circular-Risiko: receipt-actions importiert NICHT zurück hierher).
     - openReceiptActionModal() → defensive window.* Aufruf (Bootstrap-Race-safe).

   Modul-State (lokal):
     - _partialCorrectionState  (Teil-Storno-Modal-State)
     - _detailCorrectionContext (Korrektur-Bon-Modal Print-Context)
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { rerenderActiveTab } from '../../utils/dom.js';
import { callCloseOrderDetail } from './orders-modal-state.js';
import { _ensureBonArchived } from './orders-receipt-actions.js';
import { renderTseBlock, attachReceiptTseQr } from './receipt.js';
// S200 — Menü-Gruppierung (pure, Single-Source) fürs gruppierte Stornieren.
import { groupByLineUid } from '../../core/deals.js';

/** Pure: alle Teil-Storno-State-Items desselben line_uid (Menü-Geschwister). */
export function lineUidSiblings(items, lineUid) {
  if (!lineUid) return [];
  return (Array.isArray(items) ? items : []).filter(it => it && it.lineUid === lineUid);
}

// -----------------------------------------------------------------------------
// Lokaler State-Helper — gleicher Pattern wie in orders.js
// -----------------------------------------------------------------------------
function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [], settings: {} };
}

// =============================================================================
// 1. VOLL-STORNO (Order-Level)
// =============================================================================

const CORRECTION_REASONS = [
  { code: 'wrong_input',        label: 'Falsche Eingabe' },
  { code: 'customer_complaint', label: 'Kunde reklamiert' },
  { code: 'wrong_position',     label: 'Falsche Position bestellt' },
  { code: 'goodwill',           label: 'Kulanz' },
  { code: 'other',              label: 'Sonstiges' }
];

/**
 * Öffnet den Modal-Dialog zur Ausstellung eines Korrekturbelegs.
 * Aktuelles Order-Detail-Modal wird vorher geschlossen.
 */
export function openCorrectionDialog(orderId) {
  const state = getState();
  const orig = (state.orders || []).find(o => o.id === orderId);
  if (!orig) { showToast('Bestellung nicht gefunden', 'error'); return; }
  if (orig.isCorrection) {
    showToast('Korrekturbelege koennen nicht selbst storniert werden.', 'error');
    return;
  }
  if (orig.paymentStatus !== 'paid') {
    showToast('Nur bezahlte Bestellungen koennen storniert werden. Bitte zuerst stornieren.', 'error');
    return;
  }
  if (orig.correctedByOrderId) {
    showToast('Bestellung wurde bereits storniert (Beleg ' + orig.correctedByOrderId + ').', 'info');
    return;
  }

  // Bestehendes Order-Detail-Modal schließen (Cross-Module via modal-state Wrapper)
  callCloseOrderDetail();

  const num   = htmlEscape(String(orig.orderNumber || orig.id));
  const gross = ((orig.totalGrossCents || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const inv   = orig.invoiceNumber ? htmlEscape(String(orig.invoiceNumber)) : '—';

  const reasonOpts = CORRECTION_REASONS
    .map((r, i) => `<option value="${htmlEscape(r.code)}"${i === 0 ? ' selected' : ''}>${htmlEscape(r.label)}</option>`)
    .join('');

  const modal = document.createElement('div');
  modal.className = 'order-modal-overlay correction-modal-overlay';
  modal.id = 'correction-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = `
    <div class="order-modal correction-modal" onclick="event.stopPropagation()" data-order-id="${htmlEscape(orderId)}">
      <header class="order-modal-header correction-modal-header">
        <div>
          <div class="order-modal-eyebrow" style="color:var(--danger);">Stornobeleg / Korrekturbeleg</div>
          <h2 class="order-modal-title">Bestellung ${num} stornieren</h2>
        </div>
        <button class="order-modal-close" onclick="closeCorrectionDialog()" aria-label="Schließen">×</button>
      </header>

      <div class="order-modal-body">
        <section class="correction-summary">
          <p>
            Ein Stornobeleg neutralisiert die bezahlte Bestellung in voller Höhe.
            Er bekommt eine eigene Rechnungsnummer, wird TSE-signiert und ist GoBD-pflichtig.
            Die Original-Buchung bleibt unveränderlich erhalten.
          </p>
          <div class="correction-summary-grid">
            <div><span class="receipt-label">Rechnungs-Nr</span><span class="receipt-value">${inv}</span></div>
            <div><span class="receipt-label">Brutto-Original</span><span class="receipt-value">${gross} €</span></div>
            <div><span class="receipt-label">Erstattung</span><span class="receipt-value" style="color:var(--danger);">-${gross} €</span></div>
          </div>
        </section>
        ${((orig.paymentMethod || orig.payment_method) === 'stripe') ? (() => {
          // S234 T4 — Online-Zahlung: der Stornobeleg erstattet NICHT automatisch.
          const pi = orig.stripePaymentIntentId || orig.stripe_payment_intent_id || '';
          return `
        <section class="correction-stripe-warn" style="margin:10px 0;padding:10px 12px;background:#fff4e5;border:1px solid #f0c98a;border-radius:8px;color:#5a4a2e;font-size:.88rem;">
          <strong>💳 Online-Zahlung (Stripe)</strong> — der Stornobeleg neutralisiert die Buchung
          buchhalterisch, löst aber <strong>keine</strong> Rückerstattung aus. Die Erstattung muss
          <strong>manuell im Stripe-Dashboard</strong> erfolgen.
          ${pi ? `<div style="margin-top:6px;">PaymentIntent: <code style="word-break:break-all;">${htmlEscape(pi)}</code>
            <button class="btn btn-ghost btn-xs" style="margin-left:6px;" onclick="copyStripePaymentIntent('${htmlEscape(pi)}')" title="PaymentIntent-Id kopieren">📋 Kopieren</button></div>` : ''}
        </section>`;
        })() : ''}

        <section class="correction-form">
          <label class="field">
            <span>Grund für die Korrektur <span style="color:var(--danger);">*</span></span>
            <select id="correction-reason-code" class="field-input">
              ${reasonOpts}
            </select>
          </label>
          <label class="field">
            <span>Bemerkung (optional, max. 500 Zeichen)</span>
            <textarea id="correction-reason-text" class="field-input" rows="3" maxlength="500"
                      placeholder="z.B. Kundin hat Bowl reklamiert – wurde mit Croutons statt ohne ausgeliefert."></textarea>
          </label>
        </section>

        <section class="correction-actions">
          <button class="btn btn-ghost btn-sm" onclick="closeCorrectionDialog()">Abbrechen</button>
          <button class="btn btn-primary btn-sm" onclick="submitCorrectionDialog()" style="background:var(--danger-soft);border-color:var(--danger);">
            ↩ Stornobeleg jetzt ausstellen
          </button>
        </section>
      </div>
    </div>
  `;
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeCorrectionDialog();
  });
  const onKey = (e) => {
    if (e.key === 'Escape') { closeCorrectionDialog(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);
  document.body.appendChild(modal);
}

export function closeCorrectionDialog() {
  const m = document.getElementById('correction-modal');
  if (m) m.remove();
}

/**
 * Submit-Handler. Ruft die RPC, zeigt Toast, druckt anschließend den
 * Korrekturbeleg (oder zeigt das Beleg-Aktion-Modal aus Task #8 wenn
 * eingebaut). TSE-Signatur wird asynchron angestoßen.
 */
export async function submitCorrectionDialog() {
  const m = document.getElementById('correction-modal');
  if (!m) return;
  const orderId = m.querySelector('.correction-modal').getAttribute('data-order-id');
  const codeEl  = document.getElementById('correction-reason-code');
  const textEl  = document.getElementById('correction-reason-text');
  if (!orderId || !codeEl) return;
  const code = codeEl.value;
  const text = (textEl && textEl.value || '').trim();

  // Confirm-Schritt — Begründung und Verweis sichtbar
  const reasonLabel = (CORRECTION_REASONS.find(r => r.code === code) || {}).label || code;
  const ok = (await window.styledConfirm({
    title: 'Stornobeleg ausstellen?',
    body: `Grund: ${reasonLabel}${text ? '\nBemerkung: ' + text.slice(0, 80) + (text.length > 80 ? '…' : '') : ''}\n\nDer Vorgang ist GoBD-konform unveränderbar.`,
    danger: true, confirmLabel: 'Ausstellen'
  })).confirmed;
  if (!ok) return;

  // Submit-Button disable während des Calls
  const submitBtn = m.querySelector('.btn-primary');
  if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Erzeuge Stornobeleg…'; }

  try {
    const res = await window.App.storage.orders.createCorrection(orderId, code, text || null);
    if (!res || !res.id) throw new Error('Korrekturbeleg lieferte kein Ergebnis');

    // Event für Mail-/Audit-Pipeline (Phase 10a.2 Task #9)
    if (window.App.events) {
      const correctionOrder = (getState().orders || []).find(o => o.id === res.id);
      const originalOrder   = (getState().orders || []).find(o => o.id === orderId);
      await window.App.events.emit('order:corrected', {
        original: originalOrder,
        correction: correctionOrder || { id: res.id, orderNumber: res.orderNumber, invoiceNumber: res.invoiceNumber },
        reasonCode: code,
        reasonText: text || null
      });
    }

    showToast(`Stornobeleg ausgestellt · Rg. ${res.invoiceNumber}`, 'success');
    closeCorrectionDialog();

    // TSE-Signatur — AWAIT (S95-Fix). Vorher fire-and-forget (.then): der
    // Stornobeleg wurde aus dem noch UNSIGNIERTEN _state.orders-Cache-Objekt
    // gebaut und zeigte fälschlich „Keine TSE-Signatur — vorläufiger Beleg",
    // obwohl die Korrektur-Order TSE-signiert ist (GoBD-Bug, DB-verifiziert:
    // ord_…c7fa54 hatte tse_transaction_id='38', Counter 115). Jetzt erst
    // signieren, DANN frisch laden, DANN Beleg/PDF/Mail/Druck bauen. Bei
    // Signatur-Fehler: best-effort weiter (Beleg zeigt dann korrekt „vorläufig").
    const correctionOrder = (getState().orders || []).find(o => o.id === res.id);
    if (correctionOrder && typeof window.tseSignAndRecord === 'function') {
      try {
        const sigRes = await window.tseSignAndRecord(correctionOrder);
        if (sigRes && sigRes.sig) {
          showToast(`TSE-Signatur Stornobeleg (${sigRes.sig.mode}) · Counter ${sigRes.sig.signatureCounter}`, 'success');
        }
      } catch (err) {
        console.warn('[submitCorrection] TSE-Signatur fehlgeschlagen:', err);
        showToast('TSE-Signatur Stornobeleg fehlgeschlagen: ' + (err.message || err), 'error');
      }
    }

    // S95 — Korrektur-Order FRISCH aus der DB nachladen (umgeht den evtl.
    // veralteten _state.orders-Cache; Realtime hinkt nach record_tse_signature
    // 1-2s nach). Damit trägt das Objekt tseTransactionId/-Counter/-QR, BEVOR
    // Beleg/PDF/Mail/Druck gebaut werden — analog orderMarkPaid (S53 getFresh).
    // Items mitmergen, damit der Stornobeleg die Positionen zeigt. Fallback:
    // das (unsignierte) Cache-Objekt.
    let freshCorrection = await window.App.storage.orders.getFresh(res.id).catch(() => null);
    if (!freshCorrection) freshCorrection = correctionOrder;
    if (freshCorrection) {
      try {
        const items = await window.App.storage.orders.listItems(res.id);
        if (items && items.length) {
          freshCorrection = { ...freshCorrection, preorderSnapshot: {
            version: 2,
            items: items.map(it => ({
              name:             it.nameSnapshot || it.name_snapshot,
              qty:              it.qty,
              unit_price_cents: it.unitPriceCents || it.unit_price_cents,
              line_total_cents: it.lineTotalCents || it.line_total_cents,
              tax_rate:         it.taxRate || it.tax_rate,
              note:             it.notes || (it.customizations && it.customizations.note)
            }))
          } };
        }
      } catch (_) { /* nicht-fatal: Bon zeigt ggf. „Keine Positionen" */ }
    }

    rerenderActiveTab();

    // S57 Hard-Way (GoBD §147 AO): Stornobeleg-Archivierung GARANTIERT — jetzt
    // mit der signierten, frischen Order (sonst landet ein „vorläufig"-Bon
    // unveränderbar im Archiv). _ensureBonArchived ist idempotent — gleicher
    // Stornobeleg darf nicht doppelt archiviert werden. Source 'correction'.
    if (freshCorrection) {
      const archResult = await _ensureBonArchived(freshCorrection, { source: 'correction' });
      if (!archResult.archived) {
        showToast(
          '⚠️ KRITISCH: Stornobeleg-Archivierung fehlgeschlagen — ' + (archResult.error || 'Unbekannter Fehler'),
          'error'
        );
      }
    }

    // Phase 21 (Session 22 Nachschlag): immer Beleg-Aktion-Modal öffnen.
    // Modal-Funktion nach S57 nur noch UX: Versand / Druck. Archivierung
    // ist bereits garantiert oben gelaufen. Übergibt die frische, signierte
    // Order → Storno-PDF/-Mail/-Druck tragen den TSE-Block (S95).
    if (freshCorrection) {
      if (typeof window.openReceiptActionModal === 'function') {
        window.openReceiptActionModal(freshCorrection, { kind: 'correction' });
      } else {
        showToast('Beleg-Aktion-Modal nicht verfügbar. Stornobeleg wurde angelegt — bitte aus dem Bestellungs-Tab nachsehen.', 'error');
      }
    }

    rerenderActiveTab();
  } catch (e) {
    console.error('[submitCorrection]', e);
    showToast('Korrekturbeleg fehlgeschlagen: ' + (e.message || e), 'error');
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = '↩ Stornobeleg jetzt ausstellen'; }
  }
}

// =============================================================================
// 2. TEIL-STORNO (S60 Block S — GoBD §146a AO Append-Only via order_corrections)
// =============================================================================
// Modal listet alle order_items mit qty-Pickern (max=remaining), Reason-Dropdown.
// Submit erzeugt pro Item mit qty>0 eine Korrektur via create_order_correction-RPC.
// Anschließend EINE TSE-Storno-Signatur über die Summe + Korrektur-Bon-Druck.
// -----------------------------------------------------------------------------

const PARTIAL_CORRECTION_REASONS = [
  { code: 'preparation_error',  label: 'Zubereitungs-Fehler (z.B. falsche Zutat)' },
  { code: 'customer_complaint', label: 'Kundenreklamation' },
  { code: 'wrong_billing',      label: 'Falschberechnung' },
  { code: 'out_of_stock',       label: 'Ausverkauft (nach Bezahlung erkannt)' },
  { code: 'other',              label: 'Sonstiges' }
];

let _partialCorrectionState = null;   // { orderId, items: [{id, name, qty, alreadyCancelled, unitPriceCents, taxRate, qtyToCancel, lineUid, dealName}] }

/** S200 — Modal-Zeilen: nach Menü-line_uid gruppiert (Menü-Kopf + „ganzes Menü" +
 *  eingerückte Komponenten). Input-IDs idx-basiert → Gruppierung ändert die
 *  DOM-Reihenfolge, die id-Adressierung bleibt aber stabil (kein Index-Drift). */
function renderPartialCorrectionRows() {
  if (!_partialCorrectionState) return '';
  const items = _partialCorrectionState.items;
  const rowFor = (it) => {
    const idx = items.indexOf(it);
    const remaining = Math.max(0, it.qty - it.alreadyCancelled);
    const linePrice = ((it.unitPriceCents || 0) / 100).toFixed(2).replace('.', ',');
    return `
      <div class="partial-corr-row" style="display: grid; grid-template-columns: 60px 1fr 90px 100px; gap: 10px; padding: 8px; border-bottom: 1px solid var(--border); align-items: center;">
        <div style="font-weight:600;">${it.qty}×</div>
        <div>
          <div>${htmlEscape(it.name)}</div>
          <div style="font-size: .82rem; color: var(--ink-soft, #666);">à ${linePrice} €${it.alreadyCancelled > 0 ? ` · bereits storniert: ${it.alreadyCancelled}` : ''}</div>
        </div>
        <div>
          <input type="number" min="0" max="${remaining}" step="1" value="${it.qtyToCancel || 0}"
                 id="partial-corr-input-${idx}"
                 ${remaining === 0 ? 'disabled' : ''}
                 onchange="setPartialCorrectionQty(${idx}, this.value)"
                 style="width: 70px; text-align: right;">
          <span style="font-size: .8rem; color: var(--ink-soft);">/ ${remaining}</span>
        </div>
        <div style="text-align: right; color: var(--danger, #b94a48);" id="partial-corr-sum-${idx}">${((it.unitPriceCents || 0) * (it.qtyToCancel || 0) / 100).toFixed(2).replace('.', ',')} €</div>
      </div>`;
  };
  return groupByLineUid(items).map(entry => {
    if (entry.type === 'single') return rowFor(entry.item);
    const g = entry;
    return `
      <div class="partial-corr-menu">
        <div class="partial-corr-menu-head">
          <span>🎁 ${htmlEscape(g.dealName)}</span>
          <button type="button" class="btn btn-ghost btn-xs" onclick="partialSelectWholeMenu('${htmlEscape(g.lineUid)}')">ganzes Menü stornieren</button>
        </div>
        ${g.items.map(rowFor).join('')}
      </div>`;
  }).join('');
}

/** S200 — „ganzes Menü stornieren": alle Komponenten des line_uid auf ihre
 *  Rest-Menge setzen (Default-Vorauswahl; Abwahl einzelner Komponenten via
 *  qty=0 bleibt möglich = heutiges Einzel-Storno). */
export function partialSelectWholeMenu(lineUid) {
  if (!_partialCorrectionState) return;
  lineUidSiblings(_partialCorrectionState.items, lineUid).forEach(it => {
    const idx = _partialCorrectionState.items.indexOf(it);
    const remaining = Math.max(0, it.qty - it.alreadyCancelled);
    setPartialCorrectionQty(idx, remaining);
    const input = document.getElementById('partial-corr-input-' + idx);
    if (input) input.value = String(remaining);
  });
}

export async function openPartialCorrectionDialog(orderId) {
  if (!orderId) return;
  const order = (getState().orders || []).find(o => o.id === orderId);
  if (!order) {
    showToast('Bestellung nicht gefunden.', 'error');
    return;
  }
  if (order.paymentStatus !== 'paid') {
    showToast('Teil-Storno nur für bezahlte Bestellungen möglich.', 'error');
    return;
  }
  if (order.isCorrection) {
    showToast('Korrekturbelege können nicht selbst storniert werden.', 'error');
    return;
  }
  let items = [];
  let alreadyByItem = {};
  try {
    items = await window.App.storage.orders.listItems(orderId);
    // Bereits stornierte Mengen pro Item ermitteln
    const corrections = await window.App.storage.orderCorrections.listByOrder(orderId);
    alreadyByItem = (corrections || []).reduce((acc, c) => {
      acc[c.order_item_id] = (acc[c.order_item_id] || 0) + (Number(c.qty_cancelled) || 0);
      return acc;
    }, {});
  } catch (err) {
    console.error('[openPartialCorrectionDialog]', err);
    showToast('Positionen konnten nicht geladen werden: ' + (err.message || err), 'error');
    return;
  }
  if (!items.length) {
    showToast('Keine Positionen zum Stornieren.', 'error');
    return;
  }

  _partialCorrectionState = {
    orderId,
    items: items.map(it => ({
      id:               it.id,
      lineIndex:        it.lineIndex,
      name:             it.nameSnapshot || it.menuItemId || '—',
      qty:              Number(it.qty) || 0,
      alreadyCancelled: Number(alreadyByItem[it.id]) || 0,
      unitPriceCents:   Number(it.unitPriceCents) || 0,
      taxRate:          Number(it.taxRate) || 7,
      qtyToCancel:      0,
      // S200 — Menü-Klammer für die gruppierte Storno-Optik + „ganzes Menü".
      lineUid:          (it.customizations && it.customizations._dealMeta && it.customizations._dealMeta.line_uid) || null,
      dealName:         (it.customizations && it.customizations._dealMeta && it.customizations._dealMeta.name) || null
    }))
  };

  const reasonOpts = PARTIAL_CORRECTION_REASONS.map(r => `<option value="${r.code}">${r.label}</option>`).join('');
  const rows = renderPartialCorrectionRows();

  const modal = document.createElement('div');
  modal.id = 'partial-correction-modal';
  modal.className = 'modal-backdrop';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
  modal.innerHTML = `
    <div class="partial-correction-modal" style="background:var(--bg-elev);border-radius:12px;max-width:760px;width:90%;max-height:85vh;overflow-y:auto;padding:24px;" data-order-id="${htmlEscape(orderId)}">
      <h2 style="margin:0 0 4px;color:var(--danger);">↩ Teil-Storno · Bestellung ${htmlEscape(String(order.orderNumber || orderId))}</h2>
      <p style="color: var(--ink-soft, #666); font-size: .9rem; margin-bottom: 16px;">
        Wähle die Mengen, die storniert werden sollen. Pro Position wird ein eigener
        Korrekturbeleg erzeugt (GoBD §146a AO — Append-Only).
      </p>
      ${((order.paymentMethod || order.payment_method) === 'stripe') ? (() => {
        const pi = order.stripePaymentIntentId || order.stripe_payment_intent_id || '';
        return `
      <div style="margin:0 0 14px;padding:10px 12px;background:#fff4e5;border:1px solid #f0c98a;border-radius:8px;color:#5a4a2e;font-size:.88rem;">
        <strong>💳 Online-Zahlung (Stripe)</strong> — der Korrekturbeleg erstattet nicht automatisch;
        die (Teil-)Rückerstattung erfolgt <strong>manuell im Stripe-Dashboard</strong>.
        ${pi ? ` PaymentIntent: <code style="word-break:break-all;">${htmlEscape(pi)}</code>
          <button class="btn btn-ghost btn-xs" style="margin-left:6px;" onclick="copyStripePaymentIntent('${htmlEscape(pi)}')" title="PaymentIntent-Id kopieren">📋 Kopieren</button>` : ''}
      </div>`;
      })() : ''}
      <div style="display: grid; grid-template-columns: 60px 1fr 90px 100px; gap: 10px; padding: 8px; font-weight: 600; border-bottom: 2px solid var(--border); background: var(--bg-soft);">
        <div>Menge</div><div>Position</div><div>Stornieren</div><div style="text-align:right;">Betrag</div>
      </div>
      <div id="partial-corr-rows">${rows}</div>
      <div style="display:flex;justify-content:space-between;padding:12px 8px;border-top:2px solid var(--border);font-weight:600;">
        <span>Summe Stornobetrag</span>
        <span id="partial-corr-total" style="color: var(--danger, #b94a48);">0,00 €</span>
      </div>
      <section style="margin-top: 16px;">
        <label class="field">
          <span>Grund <span style="color:var(--danger);">*</span></span>
          <select id="partial-corr-reason" class="field-input">${reasonOpts}</select>
        </label>
        <label class="field" style="margin-top: 8px;">
          <span>Bemerkung (optional, max. 500 Zeichen)</span>
          <textarea id="partial-corr-text" class="field-input" rows="2" maxlength="500"
                    placeholder="z.B. Pommes waren versalzen — eine Portion erstattet"></textarea>
        </label>
      </section>
      <section style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;">
        <button class="btn btn-ghost" onclick="closePartialCorrectionDialog()">Abbrechen</button>
        <button class="btn btn-primary" onclick="submitPartialCorrectionDialog()"
                style="background:var(--danger-soft);border-color:var(--danger);">
          ↩ Teil-Storno ausstellen
        </button>
      </section>
    </div>
  `;
  modal.addEventListener('click', (e) => { if (e.target === modal) closePartialCorrectionDialog(); });
  document.body.appendChild(modal);
}

export function closePartialCorrectionDialog() {
  const m = document.getElementById('partial-correction-modal');
  if (m) m.remove();
  _partialCorrectionState = null;
}

export function setPartialCorrectionQty(idx, val) {
  if (!_partialCorrectionState || !_partialCorrectionState.items[idx]) return;
  const it = _partialCorrectionState.items[idx];
  const remaining = Math.max(0, it.qty - it.alreadyCancelled);
  let qty = Math.max(0, Math.min(remaining, parseInt(val, 10) || 0));
  it.qtyToCancel = qty;
  // Live-Sum non-destruktiv aktualisieren (Memory feedback_gg_non_destructive_render)
  const lineEl = document.getElementById(`partial-corr-sum-${idx}`);
  if (lineEl) {
    lineEl.textContent = (it.unitPriceCents * qty / 100).toFixed(2).replace('.', ',') + ' €';
  }
  const totalCents = _partialCorrectionState.items.reduce((s, x) => s + x.unitPriceCents * x.qtyToCancel, 0);
  const totalEl = document.getElementById('partial-corr-total');
  if (totalEl) totalEl.textContent = (totalCents / 100).toFixed(2).replace('.', ',') + ' €';
}

/** S63 Block S3 — Item-Level-Storno-Shortcut.
 *  Öffnet denselben Modal wie openPartialCorrectionDialog, aber mit dem
 *  spezifischen Item bereits auf qty=1 vorbelegt + Focus auf das Eingabefeld.
 *  Operator kann die Menge noch anpassen und den Grund wählen, dann submit.
 *  Verhindert Klick-für-Klick-Reibung bei „nur eine Position stornieren".
 */
export async function submitCorrectionItemDialog(orderId, itemId) {
  if (!orderId || !itemId) return;
  await openPartialCorrectionDialog(orderId);
  if (!_partialCorrectionState) return;
  const idx = _partialCorrectionState.items.findIndex(it => it.id === itemId);
  if (idx < 0) {
    showToast('Position nicht im Modal gefunden — bitte manuell auswählen.', 'error');
    return;
  }
  const picked = _partialCorrectionState.items[idx];
  // S200 — Menü-Komponente gewählt → GANZES Menü vorausgewählt (alle line_uid-
  // Geschwister auf Rest-Menge; Abwahl einzelner Komponenten via qty=0 bleibt
  // möglich). Normale Position → wie bisher qty=1 (oder remaining, wenn <1).
  if (picked.lineUid) {
    partialSelectWholeMenu(picked.lineUid);
    setTimeout(() => {
      const input = document.getElementById('partial-corr-input-' + idx);
      if (input) { input.focus(); input.select(); }
    }, 0);
    return;
  }
  const remaining = Math.max(0, picked.qty - picked.alreadyCancelled);
  setPartialCorrectionQty(idx, Math.min(1, remaining));
  setTimeout(() => {
    const input = document.getElementById('partial-corr-input-' + idx);
    if (input) {
      input.value = String(Math.min(1, remaining));
      input.focus();
      input.select();
    }
  }, 0);
}

export async function submitPartialCorrectionDialog() {
  if (!_partialCorrectionState) return;
  const { orderId, items } = _partialCorrectionState;
  const toCancel = items.filter(it => it.qtyToCancel > 0);
  if (toCancel.length === 0) {
    showToast('Keine Mengen ausgewählt.', 'error');
    return;
  }
  const reasonCode = (document.getElementById('partial-corr-reason') || {}).value || 'other';
  const reasonText = ((document.getElementById('partial-corr-text') || {}).value || '').trim();
  if (!reasonText && reasonCode === 'other') {
    showToast('Bei "Sonstiges" bitte eine Bemerkung eintragen.', 'error');
    return;
  }
  const fullReason = `${(PARTIAL_CORRECTION_REASONS.find(r => r.code === reasonCode) || {}).label || reasonCode}${reasonText ? ' — ' + reasonText : ''}`;

  const totalCents = toCancel.reduce((s, it) => s + it.unitPriceCents * it.qtyToCancel, 0);

  // S242 / HF-11 (1) — Doppelklick-Schutz VOR dem Confirm. Vorher wurde der
  // Button erst NACH der Bestätigung disabled: zwei schnelle Klicks stapelten
  // zwei styledConfirm-Dialoge, zwei Bestätigungen hätten zwei Stornos erzeugt.
  const submitBtn = document.querySelector('#partial-correction-modal .btn-primary');
  const reenable = () => {
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = '↩ Teil-Storno ausstellen'; }
  };
  if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Storniere…'; }

  const ok = (await window.styledConfirm({
    title: `${toCancel.length} Position${toCancel.length>1?'en':''} stornieren?`,
    body: `Zusammen ${(totalCents/100).toFixed(2).replace('.', ',')} €.\n\nGrund: ${fullReason}\n\nDer Storno wird als eigener Beleg mit TSE-Signatur ausgestellt (GoBD-konform unveränderbar).`,
    danger: true, confirmLabel: 'Stornieren'
  })).confirmed;
  if (!ok) { reenable(); return; }

  // Die Batch-ID entsteht EINMAL pro Vorgang und überlebt einen Retry — der
  // RPC ist darüber idempotent (zweite Verteidigungslinie gegen Doppel-Stornos).
  if (!_partialCorrectionState.batchId) {
    _partialCorrectionState.batchId = 'corrbatch_'
      + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }
  const batchId = _partialCorrectionState.batchId;
  const order = (getState().orders || []).find(o => o.id === orderId);

  let batch;
  try {
    // S242 / HF-11 (2) — EIN atomarer Call: Beleg + alle Positionen + eigene
    // Belegnummer. Vorher lief pro Position ein create_order_correction, und
    // der Beleg entstand daraus nie.
    batch = await window.App.storage.orderCorrections.createBatch({
      orderId,
      kind:           'refund',
      items:          toCancel.map(it => ({ orderItemId: it.id, qty: it.qtyToCancel })),
      reason:         fullReason,
      reasonCategory: reasonCode,   // kam vorher NIE an — alle Bestandszeilen stehen auf 'other'
      batchId
    });
    if (!batch || !batch.id) throw new Error('Korrekturbeleg lieferte kein Ergebnis');
  } catch (e) {
    console.error('[submitPartialCorrection] createBatch', e);
    if (!(e && e.operatorRequired)) {
      showToast('Teil-Storno fehlgeschlagen: ' + (e.message || e), 'error');
    }
    reenable();
    return;
  }

  showToast(
    `${toCancel.length} Position${toCancel.length>1?'en':''} storniert · `
    + `${(totalCents/100).toFixed(2).replace('.', ',')} € Erstattung`
    + (batch.invoiceNumber ? ' · Storno-Rg. ' + batch.invoiceNumber : ''),
    'success'
  );
  closePartialCorrectionDialog();

  // Die Einzel-Korrekturen für die Beleg-Zeilen nachladen (der RPC gibt nur den
  // Batch zurück).
  let createdCorrections = [];
  try {
    const all = await window.App.storage.orderCorrections.listByOrder(orderId);
    const byItem = new Map();
    for (const c of (all || [])) {
      if (c.batch_id === batch.id) byItem.set(c.order_item_id, c);
    }
    createdCorrections = toCancel.map(it => ({ item: it, correction: byItem.get(it.id) || { id: '—' } }));
  } catch (e) {
    console.warn('[submitPartialCorrection] listByOrder (nur für die Beleg-Zeilen):', e);
    createdCorrections = toCancel.map(it => ({ item: it, correction: { id: '—' } }));
  }

  // S242 / HF-11 (3) — Signatur AWAIT, damit Beleg und Archiv sie tragen.
  // Der Persist passiert server-seitig (record_correction_tse_signature_service).
  let signedBatch = batch;
  try {
    if (typeof window.tseSignAndRecordCorrection === 'function') {
      const sigRes = await window.tseSignAndRecordCorrection(batch, {
        items: toCancel.map(it => ({
          qty:              it.qtyToCancel,
          unit_price_cents: it.unitPriceCents,
          line_total_cents: it.unitPriceCents * it.qtyToCancel,
          tax_rate:         it.taxRate
        }))
      });
      if (sigRes && sigRes.sig) {
        showToast(`TSE-Storno-Signatur (${sigRes.sig.mode}) · Counter ${sigRes.sig.signatureCounter}`, 'success');
        // Frisch nachladen: Signatur + QR stehen jetzt server-seitig am Batch.
        const fresh = await window.App.storage.orderCorrections.getBatch(batch.id).catch(() => null);
        if (fresh && fresh.tseTransactionId) signedBatch = fresh;
        else signedBatch = { ...batch, ...sigToBatchFields(sigRes.sig) };
      }
    }
  } catch (err) {
    console.warn('[submitPartialCorrection] TSE-Signatur fehlgeschlagen:', err);
    showToast('TSE-Signatur Teil-Storno fehlgeschlagen: ' + (err.message || err)
      + ' — der Reconciliation-Job zieht sie nach.', 'error');
  }

  // S242 / HF-11 (4) — Archivierung NACH der Signatur. Vorher lief sie davor,
  // deshalb stand in JEDEM archivierten Teil-Storno-Bon „TSE-Signatur Pending".
  const archResult = await _archivePartialCorrectionBon(order, createdCorrections, fullReason, signedBatch);
  if (!archResult.archived) {
    showToast(
      '⚠️ KRITISCH: Korrekturbeleg-Archivierung fehlgeschlagen — ' + (archResult.error || 'Unbekannter Fehler'),
      'error'
    );
  }

  if (typeof window.openCorrectionReceiptModal === 'function') {
    window.openCorrectionReceiptModal(order, createdCorrections, fullReason, signedBatch);
  }
  rerenderActiveTab();
}

/** Signatur-Felder aus dem sig-Objekt auf die Batch-Feldnamen mappen — Fallback,
 *  wenn der Re-Read des Batches scheitert (Realtime/Netz). */
function sigToBatchFields(sig) {
  return {
    tseTransactionId:    sig.txNumber || null,
    tseSignatureCounter: sig.signatureCounter != null ? Number(sig.signatureCounter) : null,
    tseSignatureValue:   sig.signatureValue || null,
    tseQrPayload:        sig.qrPayload || null,
    tseSignedAt:         sig.timeEnd || null,
    tseMode:             sig.mode || null
  };
}

// =============================================================================
// 3. KORREKTUR-BON-RENDERER (S60 Block S4 — HTML-Preview + Druck + Archivierung)
// =============================================================================

/** Pure-Function: baut den standalone HTML-Bon für einen Teilstorno-Batch.
 *  Wird sowohl im Modal als auch in bon_archive verwendet (GoBD §147 AO).
 */
export function buildPartialCorrectionReceiptHtml(originalOrder, createdCorrections, reasonText, batch) {
  const totalCents = createdCorrections.reduce((s, c) => s + c.item.unitPriceCents * c.item.qtyToCancel, 0);
  const rows = createdCorrections.map(({ item, correction }) => `
    <tr>
      <td style="text-align:right;padding:2px 6px;">${item.qtyToCancel}×</td>
      <td style="padding:2px 6px;">${htmlEscape(item.name)}</td>
      <td style="text-align:right;padding:2px 6px;">${((item.unitPriceCents * item.qtyToCancel) / 100).toFixed(2).replace('.', ',')} €</td>
      <td style="text-align:right;padding:2px 6px;font-family:monospace;font-size:.7rem;color:var(--ink-soft);">${htmlEscape(String(correction.id || '—').slice(-8))}</td>
    </tr>
  `).join('');
  // S242 / HF-11 — derselbe TSE-Block wie auf jedem anderen Kassenbeleg,
  // inklusive QR. Vorher stand hier ein eigener Mini-Block, der den QR nur als
  // abgeschnittenen Text zeigte — und in der Praxis immer den Pending-Zweig,
  // weil der Teil-Storno nie signiert wurde.
  // `attachReceiptTseQr(root)` muss der Caller NACH dem Einfügen ins DOM rufen.
  const sigBlock = batch && batch.tseTransactionId
    ? renderTseBlock(batch)
    : `
    <div style="border:1px dashed var(--danger);padding:12px;margin-top:16px;background:var(--danger-soft);font-size:.85rem;">
      ⚠️ TSE-Signatur Pending — wird automatisch via Reconciliation-Job ergänzt.
    </div>`;
  const invoiceLine = batch && batch.invoiceNumber
    ? `<div>Storno-Rg.: <strong>${htmlEscape(String(batch.invoiceNumber))}</strong></div>`
    : '';
  return `
    <div style="border:2px solid var(--ink);padding:16px;font-family:monospace;font-size:.92rem;max-width:540px;">
      <div style="text-align:center;font-weight:bold;font-size:1rem;margin-bottom:8px;">KORREKTURBELEG (TEIL-STORNO)</div>
      <div style="text-align:center;color:var(--danger);margin-bottom:12px;font-weight:bold;">--- STORNO §146a AO ---</div>
      ${invoiceLine}
      <div>Original-Rg.: <strong>${htmlEscape(String(originalOrder.invoiceNumber || originalOrder.orderNumber || '—'))}</strong></div>
      <div>Original-Bon-ID: <code style="font-size:.78rem;">${htmlEscape(String(originalOrder.id))}</code></div>
      <div>Storno-Datum: ${new Date().toLocaleString('de-DE')}</div>
      <hr style="margin:8px 0;">
      <table style="width:100%;border-collapse:collapse;">
        <thead>
          <tr style="border-bottom:1px solid var(--ink);">
            <th style="text-align:right;width:50px;padding:2px 6px;">Menge</th>
            <th style="text-align:left;padding:2px 6px;">Position</th>
            <th style="text-align:right;width:90px;padding:2px 6px;">Betrag</th>
            <th style="text-align:right;width:80px;padding:2px 6px;font-size:.7rem;">Korr-ID</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <hr style="margin:8px 0;">
      <div style="display:flex;justify-content:space-between;font-weight:bold;color:var(--danger);font-size:1.05rem;">
        <span>ERSTATTUNG GESAMT</span>
        <span>${(totalCents / 100).toFixed(2).replace('.', ',')} €</span>
      </div>
      <div style="margin-top:10px;font-size:.82rem;padding:8px;background:var(--bg-elev);border-left:3px solid var(--danger);">
        <strong>Grund:</strong> ${htmlEscape(reasonText)}
      </div>
      ${sigBlock}
      <div style="margin-top:12px;font-size:.7rem;color:var(--ink-muted);text-align:center;font-style:italic;">
        Kassensystem: YOUR GROWTH 365 · GoBD-konform Append-Only · Original-Beleg unverändert
      </div>
    </div>
  `;
}

// Holder für aktuelle Korrektur-Kontext beim Modal-Druck.
let _detailCorrectionContext = null;
// ESC-Handler des Korrekturbeleg-Modals — beim Schließen wieder abmelden,
// sonst sammeln sich nach mehreren Teil-Stornos Listener an.
let _correctionReceiptKeyHandler = null;

export function openCorrectionReceiptModal(originalOrder, createdCorrections, reasonText, batch) {
  const receiptHtml = buildPartialCorrectionReceiptHtml(originalOrder, createdCorrections, reasonText, batch);

  // S63 Block S4 — Globale Refs für Print-Button (kein Inline-onclick-Param-Hack).
  _detailCorrectionContext = { originalOrder, createdCorrections, reasonText, batch };

  const modal = document.createElement('div');
  modal.id = 'correction-receipt-modal';
  modal.className = 'modal-backdrop';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
  // S242-HF / HF-13 — der Schließen-Button stand auf
  // `onclick="document.getElementById('correction-receipt-modal').remove()"`.
  // Inline-Handler laufen über den dom-bindings-Parser, der nur Funktions-
  // Aufrufe kennt: er meldete „Unbekanntes Statement" und tat nichts. Das Modal
  // ist `position:fixed; z-index:9999` — der Operator war nach JEDEM Teil-Storno
  // bis zum Reload blockiert (Memory feedback_gg_inline_handler_csp).
  modal.innerHTML = `
    <div style="background:var(--bg-elev);border-radius:12px;max-width:580px;width:90%;max-height:85vh;overflow-y:auto;padding:24px;"
         onclick="event.stopPropagation()">
      <h2 style="margin:0 0 8px;">📄 Korrekturbeleg</h2>
      <p style="color: var(--ink-soft, #666); font-size: .88rem; margin-bottom: 12px;">
        Bon wurde automatisch in bon_archive gespeichert. Aufbewahrung §147 AO 10 Jahre.
      </p>
      ${receiptHtml}
      <section style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;flex-wrap:wrap;">
        <button class="btn btn-ghost" onclick="closeCorrectionReceiptModal()">Schließen</button>
        <button class="btn btn-ghost" onclick="printCorrectionReceiptBrowser()" title="Browser-Druck (HTML)">🌐 Browser</button>
        <button class="btn btn-primary" onclick="printCorrectionReceiptToPrinter()" title="An aktive Bondrucker senden">🖨️ Bondrucker</button>
      </section>
    </div>
  `;
  // Backdrop-Klick + ESC wie bei den anderen Modals (closeBonPdfModal-Muster).
  modal.addEventListener('click', (e) => { if (e.target === modal) closeCorrectionReceiptModal(); });
  _correctionReceiptKeyHandler = (e) => {
    if (e.key === 'Escape') closeCorrectionReceiptModal();
  };
  document.addEventListener('keydown', _correctionReceiptKeyHandler);
  document.body.appendChild(modal);
  // S242 — QR erst nach dem Einfügen ins DOM rendern (qrcode-generator lazy).
  attachReceiptTseQr(modal).catch(err =>
    console.warn('[correction-receipt] QR-Rendering fehlgeschlagen:', err));
}

/**
 * S242-HF / HF-13 — Browser-Druck aus dem Korrekturbeleg-Modal.
 *
 * Stand vorher als `onclick="window.print()"` im Markup — der dom-bindings-
 * Parser löst nur reine Funktions-Namen auf (`fn(...)`), keine Member-Aufrufe
 * wie `window.print()`. Der Button war damit genauso tot wie „Schließen",
 * nur unauffälliger.
 */
export function printCorrectionReceiptBrowser() {
  if (typeof window.print === 'function') window.print();
}

/**
 * S242-HF / HF-13 — Korrekturbeleg-Modal schließen.
 *
 * Eigene exportierte Funktion statt eines Inline-DOM-Ausdrucks: `onclick` geht
 * über den dom-bindings-Parser, der ausschließlich Funktions-Aufrufe auflöst.
 * Sie ist unten ans `window` gebunden (wie `closeBonPdfModal`), damit der
 * Handler ohne `window.`-Präfix im Markup steht.
 */
export function closeCorrectionReceiptModal() {
  const m = document.getElementById('correction-receipt-modal');
  if (m) m.remove();
  if (_correctionReceiptKeyHandler) {
    document.removeEventListener('keydown', _correctionReceiptKeyHandler);
    _correctionReceiptKeyHandler = null;
  }
  _detailCorrectionContext = null;
}

/** S63 Block S4 — sendet den Teilstorno-Korrekturbeleg an die aktiven Receipt-
 *  Drucker (S260M via Cloud-Backend / Bridge). Backend = mock zeigt Preview. */
export async function printCorrectionReceiptToPrinter() {
  if (!_detailCorrectionContext) {
    showToast('Kein Korrektur-Kontext aktiv.', 'error');
    return;
  }
  const { originalOrder, createdCorrections, reasonText, batch } = _detailCorrectionContext;
  if (typeof window.printerPrintPartialCorrection !== 'function') {
    showToast('Drucker-Adapter nicht initialisiert.', 'error');
    return;
  }
  try {
    const res = await window.printerPrintPartialCorrection(originalOrder, createdCorrections, reasonText, batch);
    if (res && res.ok !== false) {
      showToast('Korrekturbeleg an Drucker gesendet.', 'success');
    }
  } catch (e) {
    console.error('[printCorrectionReceiptToPrinter]', e);
    showToast('Drucker-Fehler: ' + (e.message || e), 'error');
  }
}

/** Persistiert den Teilstorno-Korrekturbeleg in bon_archive (§147 AO). */
export async function _archivePartialCorrectionBon(originalOrder, createdCorrections, reasonText, batch) {
  if (!window.App || !window.App.storage || !window.App.storage.bonArchive) {
    console.error('[archivePartialCorrection] bon_archive-Adapter fehlt');
    return { archived: false, error: 'bon_archive-Adapter fehlt' };
  }
  const html = buildPartialCorrectionReceiptHtml(originalOrder, createdCorrections, reasonText, batch);
  // S242 — Beträge und Belegnummer kommen jetzt aus dem Batch (server-seitig
  // gerechnet, Steuer je Satz aus dem Pro-Satz-Brutto). Vorher wurde hier
  // parallel gerechnet — und die „Belegnummer" war die Original-Nummer mit
  // angehängtem „-PK", also gar keine Nummer aus der Sequenz.
  const b = batch || {};
  const totalCents = Number(b.totalGrossCents || 0)
    || -createdCorrections.reduce((s, c) => s + c.item.unitPriceCents * c.item.qtyToCancel, 0);
  const entry = {
    orderId:         b.id || ('corrbatch_' + originalOrder.id),
    orderNumber:     (originalOrder.orderNumber || originalOrder.id) + '-PK',  // Partial-Korrektur-Marker
    invoiceNumber:   b.invoiceNumber || ((originalOrder.invoiceNumber || '') + '-PK'),
    isCorrection:    true,
    correctsOrderId: originalOrder.id,
    customerName:    originalOrder.customerName || '',
    customerEmail:   originalOrder.customerEmail || '',
    paidAt:          new Date().toISOString(),
    paymentMethod:   b.paymentMethod || originalOrder.paymentMethod || 'cash',
    totalGrossCents: totalCents,            // negativ für Storno (analog Voll-Storno)
    tax7Cents:       Number(b.tax7Cents || 0),
    tax19Cents:      Number(b.tax19Cents || 0),
    pickupDate:      originalOrder.pickupDate || '',
    archivedAt:      new Date().toISOString(),
    html
  };
  try {
    await window.App.storage.bonArchive.upsert(entry);
    if (typeof window.audit === 'function') {
      window.audit('bon.archived_partial_correction', {
        batch_id: entry.orderId,
        invoice_number: entry.invoiceNumber,
        original_order_id: originalOrder.id,
        original_invoice: originalOrder.invoiceNumber,
        correction_count: createdCorrections.length,
        correction_ids: createdCorrections.map(c => c.correction.id),
        total_gross_cents: totalCents,
        tse_tx_number: b.tseTransactionId || null
      });
    }
    return { archived: true, batchId: entry.orderId };
  } catch (err) {
    console.error('[archivePartialCorrection]', err);
    return { archived: false, error: err.message || String(err) };
  }
}

// -----------------------------------------------------------------------------
// Backward-Compat (inline-onclick-Handler werden via dom-bindings.js und
// direkten onclick="" gegen window.* aufgelöst)
// -----------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  Object.assign(window, {
    // Voll-Storno
    openCorrectionDialog,
    closeCorrectionDialog,
    submitCorrectionDialog,
    // Teil-Storno
    openPartialCorrectionDialog,
    closePartialCorrectionDialog,
    setPartialCorrectionQty,
    partialSelectWholeMenu,   // S200 — „ganzes Menü stornieren"
    submitPartialCorrectionDialog,
    // Item-Level-Shortcut
    submitCorrectionItemDialog,
    // Korrektur-Bon-Renderer + Druck
    openCorrectionReceiptModal,
    closeCorrectionReceiptModal,
    printCorrectionReceiptBrowser,
    buildPartialCorrectionReceiptHtml,
    _archivePartialCorrectionBon,
    printCorrectionReceiptToPrinter
  });
}

// -----------------------------------------------------------------------------
// Test-Hooks
// -----------------------------------------------------------------------------

export {
  CORRECTION_REASONS as _CORRECTION_REASONS,
  PARTIAL_CORRECTION_REASONS as _PARTIAL_CORRECTION_REASONS
};
